/**
 * The Rank column's header filter for the T2 Business report's SDP view. Its own file so the
 * column builder beside it exports no component: a module mixing the two cannot hot-reload.
 */

import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import type { TopSuppliers } from './sdp-columns';

const TOP_CHOICES: readonly TopSuppliers[] = [null, 5, 10, 20];

function topLabel(value: TopSuppliers): string {
  return value === null ? 'All' : `Top ${String(value)}`;
}

/**
 * The Rank column's own filter - the one local control section 4.4 allows, and it inherits the
 * condition: offered only over a complete result, because "top 10" out of a result the server cut
 * short is the top ten of whatever happened to arrive.
 *
 * It selects rows and classifies none. The rank itself is the server's.
 */
export function RankFilter({
  value,
  onChange,
}: {
  value: TopSuppliers;
  onChange: (next: TopSuppliers) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex min-w-16 flex-col gap-0.5 py-0.5">
      <span className="whitespace-normal break-words [line-height:1.2]">Rank</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label="Show how many suppliers"
            aria-haspopup="listbox"
            aria-expanded={open}
            className={cn(
              'h-6 w-full min-w-0 justify-between gap-0.5 border-table-header-border/40 bg-background/95 px-1',
              'text-[11px] font-normal text-foreground hover:bg-background',
              value === null && 'text-muted-foreground',
            )}
            // The header is also the sort control; a click on the filter is not a click on it.
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <span className="truncate">{topLabel(value)}</span>
            <FontAwesomeIcon icon={faChevronDown} className="size-2.5 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-36 p-1" align="start">
          <ul role="listbox" className="flex flex-col">
            {TOP_CHOICES.map((choice) => (
              <li key={topLabel(choice)}>
                <button
                  type="button"
                  role="option"
                  aria-selected={choice === value}
                  className="flex w-full items-center gap-1.5 rounded-sm px-2 py-1 text-left text-xs hover:bg-accent"
                  onClick={() => {
                    onChange(choice);
                    setOpen(false);
                  }}
                >
                  <span className="w-3 shrink-0">
                    {choice === value ? (
                      <FontAwesomeIcon icon={faCheck} className="size-2.5" aria-hidden />
                    ) : null}
                  </span>
                  <span>{topLabel(choice)}</span>
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}
